import { createFileRoute } from "@tanstack/react-router";
import { DriverLandingPage, type DriverLandingContent } from "@/components/DriverLandingPage";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";
import { getPublicConfig } from "@/lib/site-content.functions";

const CONTENT: DriverLandingContent = {
  eyebrow: "Independent Value-for-Money Garage",
  h1: "Value-for-Money Garage in East London",
  intro:
    "Clear advice, fair pricing and practical repair options from Woodseer Autos — an independent value-for-money garage serving East London and E14. We'll explain what's urgent, what can wait, and how to keep your repair bill sensible.",
  trustChips: [
    "Independent — no dealer mark-up",
    "DVSA-authorised MOT station",
    "Written quotes before any work",
    "MOT, service & repairs under one roof",
    "Honest advisories",
  ],
  audienceTitle: "Who comes to us for value",
  audience: [
    "Local families keeping the household car going",
    "Working drivers covering high mileage",
    "Older vehicles that don't need dealer prices",
    "Small businesses with a van or two",
    "Anyone tired of being upsold",
    "Drivers buying or selling a used car",
  ],
  helpTitle: "What value-for-money means here",
  helpItems: [
    "Clear written quote before any work starts",
    "Urgent vs advisory repairs explained in plain English",
    "Good / better / best parts options where it makes sense",
    "No unnecessary upselling — if it doesn't need doing, we say so",
    "Service and MOT bundled when it saves you a trip",
    "Re-quote if a repair turns out smaller than expected",
  ],
  issuesTitle: "Common jobs done at sensible prices",
  issues: [
    "MOT testing",
    "Interim, full and major servicing",
    "Diagnostics for warning lights",
    "Brake pads, discs and calipers",
    "Tyre fitting and alignment",
    "Clutch and gearbox repairs",
    "Suspension, springs and bushes",
    "Bodywork and paint touch-ups",
  ],
  valueTitle: "How to keep repair costs under control",
  valueBody:
    "Diagnose before you replace. Tackle urgent jobs early so they don't turn into bigger ones. Always ask what's urgent and what's advisory. Combine MOT and service in one visit. Ask for OE-quality parts at the right price rather than the cheapest available — and avoid leaving warning lights to fester. We'll walk you through every choice before any spanners come out.",
  beforeTitle: "When not to delay a repair",
  beforeItems: [
    "Any red dashboard warning light",
    "Grinding, vibration or pull under braking",
    "Steering pulling strongly to one side",
    "Knocking from suspension over speed humps",
    "Visible tyre sidewall damage or low tread",
    "Coolant or oil leaks under the car",
    "Engine management light that keeps returning",
    "Anything that affects MOT before your test date",
  ],
  faqs: [
    { q: "Are you cheaper than a main dealer?", a: "Almost always for the same standard of work — we use OE-quality parts and stamp service books. We don't claim to be the cheapest in town, but we focus on fair pricing and not adding work you don't need." },
    { q: "Will you give me a quote before starting?", a: "Yes — in writing. No work is carried out without your approval. If a job turns out smaller than expected we re-quote before continuing." },
    { q: "Can you tell me what's urgent vs what can wait?", a: "That's the most useful thing we can do for you. Every quote is split into safety-urgent, MOT-required and advisory items so you can plan." },
    { q: "Do cheaper parts mean a worse repair?", a: "Not always — for some jobs there's a sensible budget option that lasts well. For brakes and safety components we strongly recommend OE-quality. We'll always explain the trade-off." },
    { q: "Can you help me decide whether a repair is worth doing on an older car?", a: "Yes. We'll be straight about whether a job is good value relative to the car's market value — and if it isn't, we'll say so." },
    { q: "Do you offer a warranty on the work?", a: "Workmanship warranty is available on qualifying repairs — ask when booking." },
  ],
  bookingContext: "value-for-money",
  waPrefill: "I'm looking for value-for-money advice before booking repairs. Reg: ",
  relatedLinks: [
    { to: "/cost", label: "All cost guides" },
    { to: "/pricing", label: "Pricing" },
    { to: "/mot", label: "MOT testing" },
    { to: "/servicing", label: "Servicing" },
    { to: "/diagnostics", label: "Diagnostics" },
    { to: "/mechanical-repairs", label: "Mechanical repairs" },
    { to: "/bodywork", label: "Bodywork" },
    { to: "/pco-mot-east-london", label: "PCO / private-hire support" },
  ],
};

export const Route = createFileRoute("/value-for-money-garage-east-london")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/value-for-money-garage-east-london",
      title: "Value-for-Money Garage East London | Woodseer Autos",
      description:
        "Clear advice, fair pricing and practical repair options from Woodseer Autos, an independent value-for-money garage serving East London and E14.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Value-for-Money Garage", path: "/value-for-money-garage-east-london" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        serviceSchema({
          name: "Value-for-Money Garage in East London",
          serviceType: "Automotive Repair",
          description:
            "Clear advice, fair pricing and practical repair options for East London drivers.",
          areaServed: "East London, E14",
          url: "https://woodseerautos.co.uk/value-for-money-garage-east-london",
        }),
        faqSchema(CONTENT.faqs) as Record<string, unknown>,
      ],
    }),
  component: () => (
    <DriverLandingPage content={CONTENT} path="/value-for-money-garage-east-london" />
  ),
});
